import { FC, ReactNode } from "react";
import { observer } from "mobx-react";
import {
  getNoticeOfBoardMeetingCustomPanels,
  NoticeOfBoardMeetingCustomPanel,
  NoticeOfBoardMeetingForm,
} from "./noticeBoardMeetingDomain";
import {
  CollapseList,
  DateQuestion,
  DefaultLanguageSettingsTip,
  DetailsSummaryList,
  DocumentLanguageSelector,
  DocumentViewingStatusLine,
  LocalDateTimePicker,
  NoticeDocumentAuthStatusLine,
} from "../common/commonComponents";
import { Button, IconButton, LinkButton } from "../../components-v2/Button";
import { Checkbox, MCheckboxGroup } from "../../components-v2/Checkbox";
import { Box, VBox } from "../../components-v2/Box";
import { MTextInput, TextInput } from "../../components-v2/TextInput";
import { DetailsPanel, getDecisionDetailsPanels } from "../common/commonDomain";
import { AgendaActions } from "../common/commonAgendaComponents";
import { AttachmentsPanel } from "../../components-v2/AttachmentsPanel";
import { when } from "../../utils/functions";
import { DecisionType } from "../../common/decisions";
import { useTranslation } from "../../stores/RootStoreContext";
import { AddNameAndEmailForm, RadioFieldQuestion } from "../../common/commonComponents";
import { LocalizedTextEditor } from "../../text/LocalizedTextEditor";
import { disabledSendNoticeInfo } from "../summary/summaryDomain";
import { Alert } from "../../components-v2/Alert";

const ExtraParticipants: FC<{ form: NoticeOfBoardMeetingForm }> = observer(({ form }) => {
  const { t } = useTranslation();

  return (
    <>
      <Box>
        <div>{t("documents.noticeBoardMeeting.extraRecipients")}</div>
      </Box>
      {form.extraParticipants.map((p) => (
        <Box key={p.id} alignItems={"center"} marginLeft={"md"}>
          <div>
            {p.name} ({p.email})
          </div>
          <IconButton
            icon={"trash"}
            tooltip={t("remove")}
            onClick={() => form.removeExtraParticipant(p)}
            disabled={form.isLocked}
            compact
          />
        </Box>
      ))}

      <Box>
        {!form.isLocked && form.extraParticipantFormModel ? (
          <AddNameAndEmailForm
            form={form.extraParticipantFormModel}
            submit={form.submitExtraParticipant}
            cancel={form.cancelAddExtraParticipant}
          />
        ) : (
          <LinkButton
            label={t("documents.noticeBoardMeeting.addRecipient")}
            icon={"plus"}
            onClick={form.addExtraParticipant}
            disabled={form.isLocked}
          />
        )}
      </Box>
    </>
  );
});

const SelectBoardMembers: FC<{ form: NoticeOfBoardMeetingForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <div>{t("documents.noticeBoardMeeting.selectBoardMembers")}</div>
      <Checkbox
        label={t("all")}
        onChange={(event) => form.selectedBoardMembers.toggleAll(event.target.checked)}
        checked={form.selectedBoardMembers.isAllSelected}
        disabled={form.isLocked}
      />
      <VBox>
        <CollapseList
          closeLabel={t("collapse")}
          openLabel={t("dbot.showAll")}
          initiallyExpanded={!form.selectedBoardMembers.isAllSelected}
        >
          <MCheckboxGroup model={form.selectedBoardMembers} disabled={form.isLocked} />
        </CollapseList>
      </VBox>
    </VBox>
  );
});

const SelectDeputyMembers: FC<{ form: NoticeOfBoardMeetingForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <div>{t("documents.noticeBoardMeeting.selectDeputyMembers")}</div>
      <Checkbox
        label={t("all")}
        onChange={(event) => form.selectedDeputyMembers.toggleAll(event.target.checked)}
        checked={form.selectedDeputyMembers.isAllSelected}
        disabled={form.isLocked}
      />
      <VBox>
        <CollapseList
          closeLabel={t("collapse")}
          openLabel={t("dbot.showAll")}
          initiallyExpanded={
            !form.selectedDeputyMembers.isAllSelected && !form.selectedDeputyMembers.isNoneSelected
          }
        >
          <MCheckboxGroup model={form.selectedDeputyMembers} disabled={form.isLocked} />
        </CollapseList>
      </VBox>
    </VBox>
  );
});

const SelectObservers: FC<{ form: NoticeOfBoardMeetingForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <div>{t("documents.noticeBoardMeeting.selectObservers")}</div>
      <Checkbox
        label={t("all")}
        onChange={(event) => form.selectedObservers.toggleAll(event.target.checked)}
        checked={form.selectedObservers.isAllSelected}
        disabled={form.isLocked}
      />
      <VBox>
        <CollapseList
          closeLabel={t("collapse")}
          openLabel={t("dbot.showAll")}
          initiallyExpanded={
            !form.selectedObservers.isAllSelected && !form.selectedObservers.isNoneSelected
          }
        >
          <MCheckboxGroup model={form.selectedObservers} disabled={form.isLocked} />
        </CollapseList>
      </VBox>
    </VBox>
  );
});

const MeetingForm = observer(({ form }: { form: NoticeOfBoardMeetingForm }) => {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      <RadioFieldQuestion
        field={form.meetingForm}
        fieldName={"meetingForm"}
        question={{ key: "documents.meetingform" }}
        row
        isLocked={form.isLocked}
      />
      {form.showAddress && (
        <RadioFieldQuestion
          field={form.meetingPlace}
          fieldName={"meetingPlace"}
          question={{ key: "documents.noticeBoardMeeting.noticeToBoardMeetingPlace" }}
          row
          isLocked={form.isLocked}
        />
      )}
      {form.showInlineAddress && (
        <TextInput
          label={t("documents.meetingForm.address")}
          value={form.inlineAddress.inputValue}
          onChange={form.inlineAddress.setInputValue}
          onBlur={form.inlineAddress.commit}
          readOnly={form.isLocked}
        />
      )}
      {form.showElectronicLink && (
        <RadioFieldQuestion
          field={form.meetingLinkOption}
          fieldName={"meetingLinkOption"}
          question={{ key: "documents.meetingForm.forElectronicMeetings" }}
          row
          isLocked={form.isLocked}
        />
      )}
      {form.showElectronicLinkInput && (
        <TextInput
          label={t("documents.meetingForm.inlineAddress")}
          value={form.inlineMeetingLink.inputValue}
          onChange={form.inlineMeetingLink.setInputValue}
          onBlur={form.inlineMeetingLink.commit}
          readOnly={form.isLocked}
        />
      )}
    </VBox>
  );
});

export const NoticeOfBoardMeetingAgendaItemDetails = observer(
  function NoticeOfBoardMeetingAgendaItemDetails({
    form,
    decisionId,
    renderDecisionForm,
    renderDecisionAttachment,
  }: {
    form: NoticeOfBoardMeetingForm;
    decisionId: string;
    renderDecisionForm: (decisionId: string) => ReactNode;
    renderDecisionAttachment: (decisionId: string) => ReactNode;
  }) {
    const decision = form.currentDecisions.find((d) => d.decisionId === decisionId);
    if (!decision) {
      return null;
    }

    const editor = form.decisionTextModels.find((d) => d.decisionId === decisionId);
    return (
      <VBox gap={"md"}>
        {renderDecisionForm(decisionId)}
        {decision.type !== DecisionType.freetext && editor && (
          <LocalizedTextEditor model={editor.model} />
        )}
        {renderDecisionAttachment(decisionId)}
      </VBox>
    );
  },
);

export const NoticeOfBoardMeetingDetails = observer(function NoticeOfBoardMeetingDetails({
  form,
}: {
  form: NoticeOfBoardMeetingForm;
}) {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      <DocumentLanguageSelector field={form.documentLanguage} isLocked={form.isLocked} />
      <DefaultLanguageSettingsTip
        isLocked={form.isLocked}
        hasSavedDefaultDocumentLanguage={form.context.hasSavedDefaultDocumentLanguage}
        orgNo={form.context.orgNo}
        dispatch={form.dispatch}
        dismissedDefaultDocumentLanguageTip={
          form.context.userPrefs.dismissedDefaultDocumentLanguageTip ?? false
        }
      />
      <MTextInput
        name={"boardMeetingNumber"}
        label={t("documents.noticeBoardMeeting.boardMeetingNumberHelperText")}
        prefix={"#"}
        model={form.boardMeetingNumber}
        readOnly={form.isLocked}
      />
      <DateQuestion
        question={t("documents.noticeBoardMeeting.noticeDate")}
        field={form.dateNoticeBoardMeeting}
        isLocked={form.isLocked}
      />
      <VBox>
        <div>{t("documents.noticeBoardMeeting.dateBoardMeetingHelperText")}</div>
        <LocalDateTimePicker
          dateLabel={{ key: "documents.noticeBoardMeeting.date" }}
          dateField={form.meetingStartDate}
          timeLabel={{ key: "documents.noticeBoardMeeting.startTime" }}
          timeField={form.meetingStartTime}
          endTimeLabel={{ key: "documents.noticeBoardMeeting.endTime" }}
          endTimeField={form.meetingEndTime}
          isLocked={form.isLocked}
        />
      </VBox>
      <MeetingForm form={form} />
      <LocalizedTextEditor model={form.noticeDescriptionModel} />
    </VBox>
  );
});

export const NoticeOfBoardMeetingRecipients = observer(function NoticeOfBoardMeetingRecipients({
  form,
}: {
  form: NoticeOfBoardMeetingForm;
}) {
  return (
    <VBox>
      <SelectBoardMembers form={form} />
      {form.hasDeputyBoardMembers && <SelectDeputyMembers form={form} />}
      {form.hasObservers && <SelectObservers form={form} />}
      <ExtraParticipants form={form} />
    </VBox>
  );
});

export const NoticeOfBoardMeetingDetailsPanel = observer(function NoticeOfBoardMeetingDetailsPanel({
  panel,
  form,
  renderDecisionForm,
  renderDecisionAttachment,
}: {
  panel: DetailsPanel;
  form: NoticeOfBoardMeetingForm;
  renderDecisionForm: (decisionId: string) => ReactNode;
  renderDecisionAttachment: (decisionId: string) => ReactNode;
}) {
  switch (panel.type) {
    case "decision":
      return (
        <NoticeOfBoardMeetingAgendaItemDetails
          form={form}
          decisionId={panel.id}
          renderDecisionForm={renderDecisionForm}
          renderDecisionAttachment={renderDecisionAttachment}
        />
      );
    case "custom":
      switch (panel.id) {
        case NoticeOfBoardMeetingCustomPanel.meetingDetails:
          return <NoticeOfBoardMeetingDetails form={form} />;
        case NoticeOfBoardMeetingCustomPanel.recipients:
          return <NoticeOfBoardMeetingRecipients form={form} />;
      }
  }
  return null;
});

export const NoticeOfBoardMeetingView2 = observer(function NoticeOfBoardMeetingView({
  form,
}: {
  form: NoticeOfBoardMeetingForm;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <DocumentViewingStatusLine context={form.context} dispatch={form.dispatch} />
      <NoticeDocumentAuthStatusLine context={form.context} />
      <DetailsSummaryList
        dispatch={form.dispatch}
        panels={getNoticeOfBoardMeetingCustomPanels(form)}
        isLocked={form.isLocked}
      />
      <DetailsSummaryList
        dispatch={form.dispatch}
        panels={getDecisionDetailsPanels(form)}
        isLocked={form.isLocked}
      />
      <AgendaActions
        isLocked={form.isLocked}
        context={form.context}
        dispatch={form.dispatch}
        uploadAttachments={form.uploadAttachments}
      />
      <AttachmentsPanel
        title={false}
        uploadedAttachments={Array.from(form.attachments.values())}
        onOpen={form.openAttachment}
        onDelete={form.isLocked ? undefined : form.deleteAttachmentById}
      />
      {when(form.openSendNoticeDialogAction, (action) => (
        <Button variant={"primary"} label={t(action.label)} onClick={() => form.dispatch(action)} />
      ))}
      {when(form.openNotifyNoticeChangeDialogAction, (action) => (
        <Button
          variant={"secondary"}
          label={t(action.label)}
          onClick={() => form.dispatch(action)}
        />
      ))}
      {when(disabledSendNoticeInfo(form.context), (msg) => (
        <Alert type="info" message={msg} />
      ))}
    </VBox>
  );
});
